/**
 * Cashflow Validator Service
 *
 * Client-side validation of cashflow data to ensure mathematical consistency.
 * Validates:
 * 1. Per-row mathematical identities
 * 2. DRF cascade (ending balance carries to next starting balance)
 * 3. Bond payment sums
 * 4. Principal multiple of $5,000 (warning only)
 */

import type { BondPaymentCashFlowRecord, CashFlow } from '~/types/candidate';

// ============================================================================
// Types
// ============================================================================

export type IssueSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: IssueSeverity;
  message: string;
  rowIndex?: number;
  paymentDate?: string;
  field?: string;
  expected?: number;
  actual?: number;
}

export interface CashflowValidationResult {
  passed: boolean;
  totalRows: number;
  errors: number;
  warnings: number;
  issues: ValidationIssue[];
}

// ============================================================================
// Validation Logic
// ============================================================================

const TOLERANCE = 1;

function approxEqual(a: number, b: number, tolerance = TOLERANCE): boolean {
  return Math.abs(a - b) <= tolerance;
}

function sum(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

function formatCurrency(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.round(Math.abs(n));
  return `${sign}$${abs.toLocaleString()}`;
}

function formatPercent(p: number): string {
  return `${(p * 100).toFixed(2)}%`;
}

function validateCashFlowRow(cf: CashFlow, rowIndex: number): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  const addIssue = (
    passed: boolean,
    message: string,
    field: string,
    expected?: number,
    actual?: number,
    severity: IssueSeverity = 'error'
  ) => {
    if (!passed) {
      issues.push({
        severity,
        message,
        rowIndex,
        paymentDate: cf.paymentDate,
        field,
        expected,
        actual,
      });
    }
  };

  // 1. Payment Revenue = Total Revenue x Revenue Percentage
  const expectedPaymentRevenue = Math.round(
    cf.totalRevenueForTaxYear * cf.paymentRevenuePercentage
  );
  addIssue(
    approxEqual(cf.paymentRevenuePortion, expectedPaymentRevenue),
    `Payment Revenue: ${formatCurrency(cf.totalRevenueForTaxYear)} x ${formatPercent(cf.paymentRevenuePercentage)} = ${formatCurrency(expectedPaymentRevenue)}, got ${formatCurrency(cf.paymentRevenuePortion)}`,
    'paymentRevenuePortion',
    expectedPaymentRevenue,
    cf.paymentRevenuePortion
  );

  // 2. Revenue Minus Existing Debt = Payment Revenue - Debt Payment
  const expectedNetRevenue = cf.paymentRevenuePortion - cf.debtPaymentAmount;
  addIssue(
    approxEqual(cf.revenueMinusExistingDebt, expectedNetRevenue),
    `Net Revenue: ${formatCurrency(cf.paymentRevenuePortion)} - ${formatCurrency(cf.debtPaymentAmount)} = ${formatCurrency(expectedNetRevenue)}, got ${formatCurrency(cf.revenueMinusExistingDebt)}`,
    'revenueMinusExistingDebt',
    expectedNetRevenue,
    cf.revenueMinusExistingDebt
  );

  // 3. Available for Bonds = Net Revenue - Interest Obligation
  const expectedAvailable = cf.revenueMinusExistingDebt - cf.interestObligation;
  addIssue(
    approxEqual(cf.availableForBonds, expectedAvailable),
    `Available: ${formatCurrency(cf.revenueMinusExistingDebt)} - ${formatCurrency(cf.interestObligation)} = ${formatCurrency(expectedAvailable)}, got ${formatCurrency(cf.availableForBonds)}`,
    'availableForBonds',
    expectedAvailable,
    cf.availableForBonds
  );

  // 4. Total Bond Payment = Sum of all bond payments
  const bondPayments: BondPaymentCashFlowRecord[] = Array.isArray(cf.bondPayments)
    ? cf.bondPayments
    : [];
  const sumBondPayments = sum(bondPayments.map((b) => b.totalPayment));
  addIssue(
    approxEqual(cf.totalBondPayment, sumBondPayments),
    `Total Bond Payment: sum = ${formatCurrency(sumBondPayments)}, got ${formatCurrency(cf.totalBondPayment)}`,
    'totalBondPayment',
    sumBondPayments,
    cf.totalBondPayment
  );

  // 5. For each bond: Total Payment = Principal + Total Interest
  for (const bond of bondPayments) {
    const expectedBondPayment = bond.principal + bond.totalInterest;
    addIssue(
      approxEqual(bond.totalPayment, expectedBondPayment),
      `${bond.bondSeriesName}: ${formatCurrency(bond.principal)} + ${formatCurrency(bond.totalInterest)} = ${formatCurrency(expectedBondPayment)}, got ${formatCurrency(bond.totalPayment)}`,
      `bond.${bond.bondSeriesId}.totalPayment`,
      expectedBondPayment,
      bond.totalPayment
    );
  }

  // 6. DRF Adjustment = Net Revenue - Total Bond Payment
  const expectedDrfAdjust = cf.revenueMinusExistingDebt - cf.totalBondPayment;
  addIssue(
    approxEqual(cf.drfAdjustment, expectedDrfAdjust),
    `DRF Adjust: ${formatCurrency(cf.revenueMinusExistingDebt)} - ${formatCurrency(cf.totalBondPayment)} = ${formatCurrency(expectedDrfAdjust)}, got ${formatCurrency(cf.drfAdjustment)}`,
    'drfAdjustment',
    expectedDrfAdjust,
    cf.drfAdjustment
  );

  // 7. DRF Ending = DRF Starting + DRF Adjustment
  const expectedDrfEnd = cf.drfStartingBalance + cf.drfAdjustment;
  addIssue(
    approxEqual(cf.drfEndingBalance, expectedDrfEnd),
    `DRF End: ${formatCurrency(cf.drfStartingBalance)} + ${formatCurrency(cf.drfAdjustment)} = ${formatCurrency(expectedDrfEnd)}, got ${formatCurrency(cf.drfEndingBalance)}`,
    'drfEndingBalance',
    expectedDrfEnd,
    cf.drfEndingBalance
  );

  // 8. Interest-only constraint
  if (cf.isInterestOnly) {
    const principalSum = sum(bondPayments.map((b) => b.principal));
    addIssue(
      Math.abs(principalSum) <= TOLERANCE,
      `Interest-only row has principal ${formatCurrency(principalSum)} (should be 0)`,
      'interestOnly.principal',
      0,
      principalSum
    );
  }

  // 9. Principal multiple of $5,000 (warning only)
  for (const bond of bondPayments) {
    if (Math.abs(bond.principal) > 0) {
      const isMultiple = Math.round(bond.principal) % 5000 === 0;
      if (!isMultiple) {
        addIssue(
          false,
          `${bond.bondSeriesName} principal ${formatCurrency(bond.principal)} not multiple of $5,000`,
          `bond.${bond.bondSeriesId}.principalMultiple`,
          undefined,
          bond.principal,
          'warning'
        );
      }
    }
  }

  return issues;
}

function validateCascade(cashFlows: CashFlow[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const cashFlowArray = Array.isArray(cashFlows) ? cashFlows : [];
  const sorted = cashFlowArray
    .slice()
    .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime());

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const next = i < sorted.length - 1 ? sorted[i + 1] : undefined;

    if (next) {
      // DRF cascade: current.drfEndingBalance = next.drfStartingBalance
      if (!approxEqual(current.drfEndingBalance, next.drfStartingBalance)) {
        issues.push({
          severity: 'error',
          message: `DRF Cascade: ${current.paymentDate} ending ${formatCurrency(current.drfEndingBalance)} != ${next.paymentDate} starting ${formatCurrency(next.drfStartingBalance)}`,
          rowIndex: i,
          paymentDate: current.paymentDate,
          field: 'drfEndingBalance.cascade',
          expected: next.drfStartingBalance,
          actual: current.drfEndingBalance,
        });
      }
    } else {
      // Last row: interest obligation should be 0
      if (Math.abs(current.interestObligation) > TOLERANCE) {
        issues.push({
          severity: 'error',
          message: `Last row ${current.paymentDate} has interestObligation ${formatCurrency(current.interestObligation)} (should be 0)`,
          rowIndex: i,
          paymentDate: current.paymentDate,
          field: 'interestObligation.final',
          expected: 0,
          actual: current.interestObligation,
        });
      }
    }
  }

  return issues;
}

// ============================================================================
// Main Validator Function
// ============================================================================

/**
 * Validates cashflow data for mathematical consistency
 * @param cashFlows - Array of CashFlow records to validate
 * @returns Validation result with pass/fail status and issues
 */
export function validateCashflows(cashFlows: CashFlow[]): CashflowValidationResult {
  const cashFlowArray = Array.isArray(cashFlows) ? cashFlows : [];
  const sorted = cashFlowArray
    .slice()
    .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime());

  // Per-row validation
  const rowIssues = sorted.flatMap((cf, idx) => validateCashFlowRow(cf, idx));

  // Cascade validation
  const cascadeIssues = validateCascade(sorted);

  const allIssues = rowIssues.concat(cascadeIssues);
  const errors = allIssues.filter((i) => i.severity === 'error');
  const warnings = allIssues.filter((i) => i.severity === 'warning');

  return {
    passed: errors.length === 0,
    totalRows: sorted.length,
    errors: errors.length,
    warnings: warnings.length,
    issues: allIssues,
  };
}
